import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createGlobalStyle } from 'styled-components';
import { useAppStore } from './store/useAppStore';
import { useLang } from './store/useLang';
import { useCurrencyStore } from './store/useCurrencyStore';
import { PublicAPI } from './api/public';
import { lightTheme, darkTheme } from './theme';
import './i18n';

import Header from './components/Header';
import Footer from './components/Footer';
import ScrollButtons from './components/ScrollButtons';
import CookieConsent from './components/CookieConsent';
import { useAuth } from './store/auth';

import Home from './pages/Home';
import Catalog from './pages/Catalog';
import Secondary from './pages/Secondary';
import Rent from './pages/Rent';
import ObjectCard from './pages/ObjectCard';
import TestObjects from './pages/TestObjects';
import TestDistricts from './pages/TestDistricts';
 import District from './pages/District';
 import Districts from './pages/Districts';
import Services from './pages/Services';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import SocialMedia from './pages/SocialMedia';
import SocialMediaPost from './pages/SocialMediaPost';
import News from './pages/News';
import NewsPost from './pages/NewsPost'; // <-- NEW
import Quiz from './pages/Quiz';
import Vacancies from './pages/Vacancies';
import Favorites from './pages/Favorites';
import NotFound from './pages/NotFound';
import AwardsPage from './pages/Awards';
import AwardView from './pages/AwardView';
import AboutPage from './pages/About';
import CollectionsPage from './pages/Collections';
import CollectionPage from './pages/Collection';
import Corporation from './pages/Corporation';
import Contacts from './pages/Contacts';
import Mission from './pages/Mission';
import Values from './pages/Values';
import Directions from './pages/Directions';
import Culture from './pages/Culture';
import Privacy from './pages/Privacy';
import Partner from './pages/Partner';

import SliderDemo from './pages/SliderDemo';

// === новые разделы недвижимости ===
import Foreign from './pages/Foreign';
import Suburban from './pages/Suburban';
import CommercialRent from './pages/CommercialRent';
import Mansions from './pages/Mansions';
import Parking from './pages/Parking';

// === специализированные каталоги ===
import ForeignProperties from './pages/ForeignProperties';
import SuburbanProperties from './pages/SuburbanProperties';
import ParkingProperties from './pages/ParkingProperties';
import CommercialRentProperties from './pages/CommercialRentProperties';

// === страницы услуг ===
import SellOrRentPage from './pages/services/SellOrRentPage';
import ServicePage from './pages/ServicePage';  // Универсальная страница для динамических услуг

// === страницы акций ===
import Promotions from './pages/Promotions';
import PromotionDetail from './pages/PromotionDetail';

// админка
import Admin from './pages/admin/Admin';
import AdminLogin from './pages/admin/AdminLogin';
import RequireAdmin from './routes/RequireAdmin';
import PostsTab from './pages/admin/tabs/post/PostsTab';
import LeadsTab from './pages/admin/tabs/LeadsTab';
import MediaTab from './pages/admin/tabs/MediaTab';
import CurrenciesTab from './pages/admin/tabs/CurrenciesTab';
import RegionsTab from './pages/admin/tabs/RegionsTab';
import DistrictsListPage from './pages/admin/tabs/districts/DistrictsListPage';
import DistrictEditPage from './pages/admin/tabs/districts/DistrictEditPage';
import PropertiesListPage from './pages/admin/tabs/properties/PropertiesListPage';
import ForeignPropertiesListPage from './pages/admin/tabs/properties/ForeignPropertiesListPage';
import ForeignPropertyEditPage from './pages/admin/tabs/properties/ForeignPropertyEditPage';
import ForeignPropertyCreatePage from './pages/admin/tabs/properties/ForeignPropertyCreatePage';
import SuburbanPropertiesListPage from './pages/admin/tabs/properties/SuburbanPropertiesListPage';
import SuburbanPropertyEditPage from './pages/admin/tabs/properties/SuburbanPropertyEditPage';
import SuburbanPropertyCreatePage from './pages/admin/tabs/properties/SuburbanPropertyCreatePage';
import CommercialRentPropertiesListPage from './pages/admin/tabs/properties/CommercialRentPropertiesListPage';
import CommercialRentPropertyEditPage from './pages/admin/tabs/properties/CommercialRentPropertyEditPage';
import CommercialRentPropertyCreatePage from './pages/admin/tabs/properties/CommercialRentPropertyCreatePage';
import ParkingPropertiesListPage from './pages/admin/tabs/properties/ParkingPropertiesListPage';
import ParkingPropertyEditPage from './pages/admin/tabs/properties/ParkingPropertyEditPage';
import ParkingPropertyCreatePage from './pages/admin/tabs/properties/ParkingPropertyCreatePage';
import PropertyEditPage   from './pages/admin/tabs/parts/PropertyEditPage';
import CatalogsListPage from './pages/admin/tabs/catalogs/CatalogsListPage';
import CatalogEditPage  from './pages/admin/tabs/catalogs/CatalogEditPage';
import CollectionsListPage from './pages/admin/tabs/collections/CollectionsListPage';
import CollectionEditPage  from './pages/admin/tabs/collections/CollectionEditPage';
import AwardsListPage from './pages/admin/tabs/awards/AwardsListPage';
import AwardEditPage  from './pages/admin/tabs/awards/AwardEditPage';
import ManagersListPage from './pages/admin/tabs/managers/ManagersListPage';
import ManagerEditPage  from './pages/admin/tabs/managers/ManagerEditPage';
import AmenitiesListPage from './pages/admin/tabs/amenities/AmenitiesListPage';
import AmenityEditPage  from './pages/admin/tabs/amenities/AmenityEditPage';
import CorporationTab from './pages/admin/tabs/CorporationTab';
import ServicesTab from './pages/admin/tabs/ServicesTab';

// ScrollToTop компонент для автоматической прокрутки наверх при смене маршрута
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

const Global = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Rubik:wght@400;500;700&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap');

  :root { color-scheme: ${({ theme }) => (theme.name === 'dark' ? 'dark' : 'light')}; }
  * { box-sizing: border-box; transition: background-color .25s ease,color .25s ease,border-color .25s ease,box-shadow .25s ease; }
  html, body, #root { height: 100%; }
  html { scroll-behavior: smooth; }
  body {
    margin: 0; overflow-x: hidden;
    font-family: ${({ $lang }) => ($lang === 'en' ? "'Poppins', Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif"
                                           : "Inter, Rubik, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif")};
    font-size: 17px; line-height: 1.55;
    background: ${({ theme }) => theme.bg}; color: ${({ theme }) => theme.fg};
    --card: ${({ theme }) => theme.card};
    --bdr: ${({ theme }) => theme.border};
    --muted: ${({ theme }) => theme.muted};
    --primary: ${({ theme }) => theme.primary};
    --primaryFg: ${({ theme }) => theme.primaryFg};
    --radius: ${({ theme }) => theme.radius};
    --shadow: ${({ theme }) => theme.shadow};
    --fg: ${({ theme }) => theme.fg};
    --gold: linear-gradient(to right, #ecbf79, #c7924c);
  }
  h1 { font-size: 40px; line-height: 1.15; margin: 0 0 14px; }
  h2 { font-size: 28px; line-height: 1.2;  margin: 0 0 12px; }
  h3 { font-size: 22px; line-height: 1.25; margin: 0 0 10px; }
  a { color: inherit; text-decoration: none; }
  img { max-width: 100%; display: block; }
  .container { width: min(1400px, 96vw); margin: 0 auto; padding: 0 18px; }
  button, .btn {
    display: inline-flex; align-items: center; justify-content: center;
    padding: 10px 14px; border-radius: var(--radius);
    border: 1px solid var(--bdr); background: var(--card);
    color: inherit; cursor: pointer; transition: .2s ease; box-shadow: var(--shadow);
    font-weight: 600;
  }
  button:hover, .btn:hover { transform: translateY(-1px); box-shadow: 0 12px 28px rgba(0,0,0,.12); }
  .btn-primary { background: var(--primary); color: var(--primaryFg); border-color: transparent; }
  .btn-ghost { background: transparent; border-color: var(--bdr); }
  input, select, textarea {
    padding: 12px 14px; border-radius: 14px; border: 1px solid var(--bdr);
    background: var(--card); color: inherit; outline: none; transition: .2s;
    min-width: 160px; appearance: none; font-size: 16px;
  }
  input:focus, select:focus, textarea:focus { box-shadow: 0 0 0 3px rgba(176,141,87,.25); border-color: var(--primary); }
  option { background: var(--card); color: var(--fg); }
  
  /* Единообразные шрифты для валютных символов */
  .price, [class*="price"], [class*="Price"] {
    font-feature-settings: "tnum" 1, "lnum" 1;
  }
  
  /* Принудительное использование системного шрифта для всех валютных символов */
  .currency-symbol,
  .price *,
  [class*="price"] *,
  [class*="Price"] * {
    font-family: ${({ $lang }) => ($lang === 'en' ? "'Poppins', Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif"
                                           : "Inter, Rubik, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif")} !important;
    font-variant-numeric: tabular-nums lining-nums;
    -webkit-font-feature-settings: "tnum" 1, "lnum" 1;
    font-feature-settings: "tnum" 1, "lnum" 1;
  }
  
  /* Специальные правила для валютных символов */
  .currency-symbol {
    display: inline-block;
    font-weight: inherit;
    font-style: normal;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

export default function App() {
  const themeName = useAppStore(s => s.theme);
  const setThemeFromStorage = useAppStore(s => s.setThemeFromStorage);
  const lang = useLang(s => s.lang);
  const setRates = useCurrencyStore(s => s.setRates);

  useEffect(() => setThemeFromStorage(), [setThemeFromStorage]);
  const theme = themeName === 'dark' ? darkTheme : lightTheme;

  const booted = useAuth(s => s.booted);
  const boot   = useAuth(s => s.boot);
  React.useEffect(() => { if (!booted) boot(); }, [booted, boot]);

  // Загрузка курсов валют при инициализации приложения
  React.useEffect(() => {
    let cancelled = false;
    async function loadCurrencyRates() {
      try {
        const data = await PublicAPI.navigation();
        const currencies = data?.currencies || [];
        
        if (currencies.length > 0 && !cancelled) {
          // Преобразуем массив валют в объект курсов
          const rates = {};
          currencies.forEach(currency => {
            if (currency.code && currency.rate_to_base != null) {
              rates[currency.code] = parseFloat(currency.rate_to_base);
            }
          });
          
          setRates(rates);
        } else {
          }
      } catch (error) {
        console.warn('Failed to load currency rates from server:', error.message);
        }
    }
    
    loadCurrencyRates();
    return () => { cancelled = true; };
  }, [setRates]);

  // Добавляем глобальный доступ к store для отладки
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.useCurrencyStore = useCurrencyStore;
      }
  }, []);

  useEffect(() => { document.body.dataset.theme = themeName; }, [themeName]);

  return (
    <ThemeProvider theme={theme}>
      <Global $lang={lang} />
      <BrowserRouter>
        <ScrollToTop />
        <Header />

        {!booted ? (
          <div className="container" style={{ padding: 24, opacity: .7 }}>
            Загрузка…
          </div>
        ) : (
          <Routes>
            {/* Публичные страницы */}
            <Route path="/" element={<Home />} />
            <Route path="/collections" element={<CollectionsPage />} />
            <Route path="/collections/:slug" element={<CollectionPage />} />
            
            <Route path="/search" element={<Catalog type="primary" />} />
            <Route path="/realty" element={<Catalog type="primary" />} />
            <Route path="/realty/newbuilds" element={<Catalog type="primary" />} />
            <Route path="/realty/secondary" element={<Secondary />} />
            <Route path="/realty/commercial" element={<Catalog type="commercial" />} />
            <Route path="/realty/rent" element={<Rent />} />
            <Route path="/secondary" element={<Secondary />} />
            <Route path="/commercial" element={<Catalog type="commercial" />} />
            <Route path="/foreign" element={<Foreign />} />
            <Route path="/suburban" element={<Suburban />} />
            <Route path="/mansions" element={<Mansions />} />
            <Route path="/parking" element={<Parking />} />
            
            {/* Специализированные каталоги */}
            <Route path="/foreign-properties" element={<ForeignProperties />} />
            <Route path="/suburban-properties" element={<SuburbanProperties />} />
            <Route path="/parking-properties" element={<ParkingProperties />} />
            <Route path="/commercial-rent-properties" element={<CommercialRentProperties />} />
            <Route path="/commercial-rent" element={<CommercialRent />} />
            <Route path="/rent" element={<Rent />} />
            <Route path="/land" element={<Catalog type="land" />} />
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/realty/:id" element={<ObjectCard />} />
            <Route path="/test-objects" element={<TestObjects />} />
            <Route path="/test-districts" element={<TestDistricts />} />
            <Route path="/districts/:id" element={<District />} />
            <Route path="/districts" element={<Districts />} />

            {/* Услуги */}
            <Route path="/services" element={<Services />} />
            {/* Динамический роут для всех услуг из админки */}
            <Route path="/services/:slug" element={<ServicePage />} />

            {/* Акции */}
            <Route path="/promotions" element={<Promotions />} />
            <Route path="/promotions/:slug" element={<PromotionDetail />} />

            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/socialmedia" element={<SocialMedia />} />
            <Route path="/socialmedia/:slug" element={<SocialMediaPost />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<NewsPost />} />   {/* <-- добавлен просмотр новости */}
            <Route path="/quiz" element={<Quiz />} />
            <Route path="/vacancies" element={<Vacancies />} />
            <Route path="/map" element={<Catalog type="map" />} />
            <Route path="/awards" element={<AwardsPage />} />
            <Route path="/awards/:id" element={<AwardView />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/about/:slug" element={<AboutPage />} />
            <Route path="/corporation" element={<Corporation />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/partners/:slug" element={<Partner />} />
            <Route path="/mission" element={<Mission />} />
            <Route path="/values" element={<Values />} />
            <Route path="/directions" element={<Directions />} />
            <Route path="/culture" element={<Culture />} />
            
            {/* Демо слайдера */}
            <Route path="/slider-demo" element={<SliderDemo />} />

            {/* Админка */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route element={<RequireAdmin />}>
              <Route path="/admin" element={<Admin />}>
                <Route index element={<PostsTab />} />
                <Route path="posts" element={<PostsTab />} />
                <Route path="leads" element={<LeadsTab />} />
                <Route path="media" element={<MediaTab />} />
                <Route path="currencies" element={<CurrenciesTab />} />
                <Route path="regions" element={<RegionsTab />} />
                <Route path="districts" element={<DistrictsListPage />} />
                <Route path="districts/new" element={<DistrictEditPage />} />
                <Route path="districts/:id" element={<DistrictEditPage />} />
                <Route path="catalogs" element={<CatalogsListPage />} />
                <Route path="catalogs/new" element={<CatalogEditPage />} />
                <Route path="catalogs/:id" element={<CatalogEditPage />} />
                <Route path="collections" element={<CollectionsListPage />} />
                <Route path="collections/new" element={<CollectionEditPage />} />
                <Route path="collections/:id" element={<CollectionEditPage />} />
                <Route path="awards" element={<AwardsListPage />} />
                <Route path="awards/new" element={<AwardEditPage />} />
                <Route path="awards/:id" element={<AwardEditPage />} />
                <Route path="properties" element={<PropertiesListPage />} />
                <Route path="properties/new" element={<PropertyEditPage />} />
                <Route path="properties/:id" element={<PropertyEditPage />} />
                <Route path="foreign">
                  <Route index element={<ForeignPropertiesListPage />} />
                  <Route path="create" element={<ForeignPropertyCreatePage />} />
                  <Route path=":id" element={<ForeignPropertyEditPage />} />
                </Route>
                <Route path="suburban">
                  <Route index element={<SuburbanPropertiesListPage />} />
                  <Route path="create" element={<SuburbanPropertyCreatePage />} />
                  <Route path=":id" element={<SuburbanPropertyEditPage />} />
                </Route>
                <Route path="commercial-rent">
                  <Route index element={<CommercialRentPropertiesListPage />} />
                  <Route path="create" element={<CommercialRentPropertyCreatePage />} />
                  <Route path=":id" element={<CommercialRentPropertyEditPage />} />
                </Route>
                <Route path="parking">
                  <Route index element={<ParkingPropertiesListPage />} />
                  <Route path="create" element={<ParkingPropertyCreatePage />} />
                  <Route path=":id" element={<ParkingPropertyEditPage />} />
                </Route>
                <Route path="managers" element={<ManagersListPage />} />
                <Route path="managers/new" element={<ManagerEditPage />} />
                <Route path="managers/:id" element={<ManagerEditPage />} />
                <Route path="amenities" element={<AmenitiesListPage />} />
                <Route path="amenities/new" element={<AmenityEditPage />} />
                <Route path="amenities/:id" element={<AmenityEditPage />} />
                <Route path="corporation" element={<CorporationTab />} />
                <Route path="services" element={<ServicesTab />} />
              </Route>
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        )}

        <Footer />
        <ScrollButtons />
        <CookieConsent />
      </BrowserRouter>
    </ThemeProvider>
  );
}
